// frontend/src/components/ImmersiveStepCard.js
import React, { useState } from 'react';
import api from '../api';
import './ImmersiveStepCard.css';
import ImageLightbox from './ImageLightbox';
import { useNotification } from '../context/NotificationContext';
import ReadableText from './ReadableText';

const ImmersiveStepCard = ({ step, index, taskId, onUpdate }) => {
  const { addNotification } = useNotification();
  const [loadingImg, setLoadingImg] = useState(false);
  const [loadingSubsteps, setLoadingSubsteps] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const stepObj = typeof step === 'string'
    ? { text: step, title: `Paso ${index + 1}`, completed: false, substeps: [] }
    : (step || {});

  const isCompleted = !!stepObj.completed;

  const toggleComplete = async (e) => {
    e.stopPropagation();
    const updatedStep = { ...stepObj, completed: !isCompleted };
    onUpdate(index, updatedStep);
  };

  const handleGenerateImage = async () => {
    setLoadingImg(true);
    try {
      const res = await api.post(`/tareas/${taskId}/steps/${index}/image`);
      if (res.data.ok && res.data.step) {
        onUpdate(index, res.data.step);
      }
    } catch (err) {
      addNotification(err.response?.data?.error || 'Error al generar imagen', 'error');
    } finally {
      setLoadingImg(false);
    }
  };

  const handleGenerateSubsteps = async () => {
    setLoadingSubsteps(true);
    try {
      const res = await api.post(`/tareas/${taskId}/steps/${index}/substeps`);
      if (res.data.ok && res.data.step) {
        onUpdate(index, res.data.step);
      }
    } catch (err) {
      addNotification(err.response?.data?.error || 'Error al generar subpasos', 'error');
    } finally {
      setLoadingSubsteps(false);
    }
  };

  const toggleSubstep = (subIdx) => {
    const newSubsteps = [...(stepObj.substeps || [])];
    const target = newSubsteps[subIdx];
    if (typeof target === 'string') {
      newSubsteps[subIdx] = { text: target, completed: true };
    } else if (target && typeof target === 'object') {
      newSubsteps[subIdx] = { ...target, completed: !target.completed };
    }
    onUpdate(index, { ...stepObj, substeps: newSubsteps });
  };

  return (
    <div className={`immersive-step-card ${isCompleted ? 'immersive-completed' : ''}`}>
      <div className="immersive-step-card-header">
        <div className="immersive-step-info">
          <span className="immersive-step-number">{index + 1}</span>
          <button
            className={`immersive-complete-toggle ${isCompleted ? 'immersive-checked' : ''}`}
            onClick={toggleComplete}
            title={isCompleted ? "Marcar como pendiente" : "Marcar como completado"}
          >
            {isCompleted && <span className="immersive-check-icon">✔</span>}
          </button>
        </div>
      </div>

      <div className="immersive-step-card-body">
        <h3 className="immersive-step-title"><ReadableText text={stepObj.title || `Paso ${index + 1}`} label="Leer título del paso">{stepObj.title || `Paso ${index + 1}`}</ReadableText></h3>
        <p className="immersive-step-text"><ReadableText text={stepObj.text} label="Leer paso de tarea">{stepObj.text}</ReadableText></p>

        {stepObj.imageUrl && (
          <div className="immersive-step-image-wrapper">
            <img
              src={stepObj.imageUrl}
              alt={stepObj.title}
              className="immersive-step-image"
              style={{ cursor: 'zoom-in' }}
              onClick={() => setShowLightbox(true)}
            />
          </div>
        )}

        {stepObj.substeps && stepObj.substeps.length > 0 && (
          <div className="immersive-substeps-list">
            {stepObj.substeps.map((sub, sidx) => {
              const isSubCompleted = typeof sub === 'string' ? false : !!sub.completed;
              const substepText = typeof sub === 'string' ? sub : (sub?.text || '');
              return (
                <div
                  key={sidx}
                  className={`immersive-substep-item ${isSubCompleted ? 'immersive-sub-completed' : ''}`}
                  onClick={() => toggleSubstep(sidx)}
                >
                  <span className="immersive-substep-bullet">{isSubCompleted ? '●' : '○'}</span>
                  <span className="immersive-substep-text">
                    <ReadableText text={substepText} label="Leer sub-paso de tarea">{substepText}</ReadableText>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="immersive-step-card-footer">
        {(!stepObj.imageUrl) && (
          <button
            className="immersive-btn-step-ai"
            onClick={handleGenerateImage}
            disabled={loadingImg}
          >
            {loadingImg ? <div className="immersive-spinner-mini" /> : '✨ Ver Paso'}
          </button>
        )}
        {(!stepObj.substeps || stepObj.substeps.length === 0) && (
          <button
            className="immersive-btn-step-ai"
            onClick={handleGenerateSubsteps}
            disabled={loadingSubsteps}
          >
            {loadingSubsteps ? <div className="immersive-spinner-mini" /> : '✨ Detallar'}
          </button>
        )}
      </div>
      {showLightbox && stepObj.imageUrl && (
        <ImageLightbox
          src={stepObj.imageUrl}
          alt={stepObj.title}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </div>
  );
};

export default ImmersiveStepCard;
